import { Button } from "antd/es";
import isFunction from "lodash/isFunction";
import React from "react";

import { Dropdown, type DropdownProps } from "~/components/dropdown";
import { IconChevronDown, IconFunnel, IconReloadOutlined } from "~/components/icon";
import { Truncated } from "~/components/truncated";
import { cx } from "~/tools/class-variance";
import { type Impulse, useScoped } from "~/tools/impulse";
import { isPresent } from "~/tools/is-present";

import { DropdownMenu } from "../dropdown-menu-for-button/DropdownMenu";
import { useClosestRelativeContainer } from "../react/RelativeContainer";

import { DEFAULT_DROPDOWN_FILTER, type DropdownFilterItem, isDefaultDropdownFilter } from "./_item";

interface DropdownFilterCommonProps extends Pick<DropdownProps, "placement"> {
	label: string;
	isLoading?: boolean;
}

interface DropdownFilterMenuProps extends DropdownFilterCommonProps {
	items: Array<DropdownFilterItem>;
	selectedItem: Impulse<DropdownFilterItem>;
}

const DropdownFilterMenu: React.FC<DropdownFilterMenuProps> = ({
	isLoading = false,
	placement = "bottomLeft",
	label,
	items,
	selectedItem,
}) => {
	const container = useClosestRelativeContainer();
	const selectedItemValue = useScoped(selectedItem);

	return (
		<DropdownMenu
			destroyPopupOnHide
			disabled={isLoading}
			getPopupContainer={() => container ?? document.body}
			initialSelectedKey={selectedItemValue.value}
			menuItems={[DEFAULT_DROPDOWN_FILTER, ...items]}
			onChange={(value) => selectedItem.setValue(value)}
			placement={placement}
			selectedKey={selectedItemValue.value}
			toKey={({ value }) => value}
			toTitle={({ label }) => label}
		>
			<span>
				<DropdownFilterHost
					fallbackValue={selectedItemValue.label}
					isLoading={isLoading}
					label={label}
					value={isDefaultDropdownFilter(selectedItemValue) ? undefined : selectedItemValue.label}
				/>
			</span>
		</DropdownMenu>
	);
};

interface DropdownFilterOverlayProps
	extends Pick<DropdownProps, "destroyPopupOnHide" | "overlay" | "align">,
		DropdownFilterCommonProps {
	selectedLabel: undefined | string;
	fallbackSelectedLabel?: string;
}

const DropdownFilterOverlay: React.FC<DropdownFilterOverlayProps> = ({
	label,
	isLoading = false,
	placement = "bottomLeft",
	align,
	overlay,
	selectedLabel,
	fallbackSelectedLabel,
	destroyPopupOnHide = true,
}) => {
	const container = useClosestRelativeContainer();

	// Without the visible state, clicking an element inside the overlay closes it
	const [visible, setVisible] = React.useState(false);

	return (
		<Dropdown
			align={align}
			destroyPopupOnHide={destroyPopupOnHide}
			disabled={isLoading}
			getPopupContainer={() => container ?? document.body}
			onVisibleChange={setVisible}
			overlay={() => (
				<div className="flex h-full w-full items-start justify-end pb-4">
					<div className="w-[400px] rounded-b bg-white p-4 shadow-md">{isFunction(overlay) ? overlay() : overlay}</div>
				</div>
			)}
			placement={placement}
			trigger={["click"]}
			visible={visible}
		>
			<span>
				<DropdownFilterHost
					fallbackValue={fallbackSelectedLabel}
					isLoading={isLoading}
					label={label}
					value={selectedLabel}
				/>
			</span>
		</Dropdown>
	);
};

const DropdownFilterHost = React.forwardRef<
	HTMLButtonElement,
	{
		isLoading: boolean;
		label: string;
		value: undefined | string;
		fallbackValue?: string;
	}
>(({ isLoading, label, value, fallbackValue }, ref) => {
	const visibleValue = value ?? fallbackValue;

	return (
		<Button
			aria-label={`${label}: ${visibleValue}`}
			className="flex min-w-0 max-w-[300px] items-center pr-2.5 pl-3 text-13"
			icon={isLoading ? <IconReloadOutlined className="[&>svg]:animate-spin" /> : <IconFunnel className="shrink-0" />}
			ref={ref}
			size="middle"
		>
			<div className="mr-1 ml-2 inline-flex min-w-0">
				<Truncated>{label}</Truncated>
				{isPresent(visibleValue) ? ":\u00A0" : ""}
				{isPresent(visibleValue) && (
					<Truncated className={cx(!isLoading && isPresent(value) && "text-primary-600")}>{visibleValue}</Truncated>
				)}
			</div>
			<IconChevronDown />
		</Button>
	);
});

export const DropdownFilter: React.FC<DropdownFilterMenuProps | DropdownFilterOverlayProps> = (props) => {
	if ("items" in props) {
		return <DropdownFilterMenu {...props} />;
	}

	return <DropdownFilterOverlay {...props} />;
};
